// MTXbudget: pure calculation core (no I/O, no dependencies), shared by the
// screens and by the headless alert job. All amounts are integer cents; an
// outflow is negative, as everywhere else in Actual. Dates are 'YYYY-MM-DD'.

export type PayConfig = {
  /** Hourly rate, in cents. */
  hourlyRate: number;
  /** Weekly hours paid at the regular rate; hours above are overtime. */
  regularHoursPerWeek: number;
  overtimeMultiplier: number;
  /** Hours the employer guarantees each week. */
  minHoursPerWeek: number;
  /** Fixed bonus added to every pay, in cents. */
  bonusPerPay: number;
  /** Deductions that do not depend on gross pay, in cents per pay. */
  fixedDeductions: number;
  /** Capped contributions (pension, employment insurance), in cents per pay. */
  cappedContributions: number;
  /** Observed (gross, income tax) points from real pay stubs, in cents. */
  taxPoints: { gross: number; tax: number }[];
  /** Date of any known payday; paydays repeat every 14 days. */
  payday: string;
  /** Days between the end of a pay period and its payday. */
  payDelayDays: number;
};

export type Occurrence = {
  id: string;
  name: string;
  date: string;
  /** Signed cents: negative for a charge, positive for income. */
  amount: number;
  /** The schedule it comes from, its account, and whether its amount varies. */
  scheduleId?: string;
  account?: string;
  approximate?: boolean;
  /** False for a one-off, which no later month will see again. */
  recurring?: boolean;
};

export type PayInput = {
  /** Net amount actually received, when known. */
  received?: number;
  /** Hours worked in each of the two weeks, when declared. */
  hours?: [number, number];
};

export type Coverage =
  | { kind: 'covered'; payday: string }
  | { kind: 'set-aside'; payday: string }
  | { kind: 'short'; missing: number };

export type PlannedCharge = Occurrence & { coverage: Coverage };

export type Fortnight = {
  payday: string;
  nextPayday: string;
  periodStart: string;
  periodEnd: string;
  /** 'received' once the net is known, 'worked' once the period is over. */
  status: 'received' | 'worked' | 'open' | 'future';
  net: number;
  netIsEstimate: boolean;
  charges: PlannedCharge[];
  totalCharges: number;
  carriedIn: number;
  /** Net this pay must bring so that every charge up to the horizon is paid. */
  requiredNet: number;
  /** Weekly hours that bring `requiredNet`. */
  thresholdHoursPerWeek: number;
  /** What is left once every charge up to the horizon is paid. */
  margin: number;
  carriedOut: number;
};

const DAY_MS = 86_400_000;

function toTime(date: string): number {
  return Date.parse(`${date}T00:00:00Z`);
}

export function addDays(date: string, days: number): string {
  return new Date(toTime(date) + days * DAY_MS).toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((toTime(to) - toTime(from)) / DAY_MS);
}

/** Income tax withheld on a gross pay, interpolated between observed points. */
export function withheldTax(config: PayConfig, gross: number): number {
  const points = [...config.taxPoints].sort((a, b) => a.gross - b.gross);
  if (points.length === 0) {
    return 0;
  }
  if (points.length === 1) {
    return Math.max(0, Math.round((points[0].tax / points[0].gross) * gross));
  }
  let index = points.findIndex(p => gross <= p.gross) - 1;
  if (index === -2) {
    index = points.length - 2;
  }
  index = Math.max(0, index);
  const a = points[index];
  const b = points[index + 1];
  const slope = (b.tax - a.tax) / (b.gross - a.gross);
  return Math.max(0, Math.round(a.tax + slope * (gross - a.gross)));
}

export function grossPay(config: PayConfig, hours: [number, number]): number {
  const regular = hours.reduce(
    (sum, h) => sum + Math.min(h, config.regularHoursPerWeek),
    0,
  );
  const overtime = hours.reduce(
    (sum, h) => sum + Math.max(0, h - config.regularHoursPerWeek),
    0,
  );
  return Math.round(
    regular * config.hourlyRate +
      overtime * config.hourlyRate * config.overtimeMultiplier +
      config.bonusPerPay,
  );
}

export function netPay(config: PayConfig, hours: [number, number]): number {
  const gross = grossPay(config, hours);
  return (
    gross -
    withheldTax(config, gross) -
    config.fixedDeductions -
    config.cappedContributions
  );
}

/** Weekly hours (same both weeks) needed to bring home `requiredNet`. */
export function hoursForNet(config: PayConfig, requiredNet: number): number {
  if (requiredNet <= 0) {
    return 0;
  }
  let low = 0;
  let high = 100;
  for (let i = 0; i < 40; i++) {
    const mid = (low + high) / 2;
    if (netPay(config, [mid, mid]) < requiredNet) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return Math.ceil(high * 10 - 1e-6) / 10;
}

/** The first payday on or after `date`. */
export function nextPayday(config: PayConfig, date: string): string {
  const offset = ((daysBetween(config.payday, date) % 14) + 14) % 14;
  return offset === 0 ? date : addDays(date, 14 - offset);
}

/** The payday that opened the fortnight `date` falls in. */
export function fortnightStart(config: PayConfig, date: string): string {
  const payday = nextPayday(config, date);
  return payday === date ? date : addDays(payday, -14);
}

/** The Sunday a week of pay starts on, for the day `date`. */
export function weekStart(date: string): string {
  const day = new Date(`${date}T00:00:00Z`);
  return addDays(date, -day.getUTCDay());
}

/**
 * Whether a bank's data stands at a day of `today`'s week: one read a week,
 * on Sunday, is enough to plan it, whatever the connection does since. The
 * day is the bank's own, not the time of a read that may bring nothing new.
 */
export function readThisWeek(dataDate: string | null, today: string): boolean {
  return dataDate !== null && dataDate >= weekStart(today);
}

export function planFortnights({
  config,
  today,
  from = today,
  occurrences,
  pays = {},
  available = 0,
  count = 3,
  incomes = [],
}: {
  config: PayConfig;
  today: string;
  /** The day the balances stand at: the first pay is the one on or after it. */
  from?: string;
  /** Scheduled occurrences from `from` onwards; income is ignored. */
  occurrences: Occurrence[];
  /** What is known about each pay, keyed by payday. */
  pays?: Record<string, PayInput>;
  /** Cash already on hand before the first payday, in cents. */
  available?: number;
  count?: number;
  /** Money other than the salary, counted in the pay it arrives in. */
  incomes?: { amount: number; date: string }[];
}): Fortnight[] {
  const minHours: [number, number] = [
    config.minHoursPerWeek,
    config.minHoursPerWeek,
  ];
  const first = nextPayday(config, from);
  const charges = occurrences
    .filter(o => o.amount < 0)
    .sort((a, b) => a.date.localeCompare(b.date));

  const drafts = Array.from({ length: count }, (_, i) => {
    const payday = addDays(first, 14 * i);
    const periodEnd = addDays(payday, -config.payDelayDays);
    const periodStart = addDays(periodEnd, -13);
    const input = pays[payday] ?? {};
    const status: Fortnight['status'] =
      input.received !== undefined
        ? 'received'
        : today > periodEnd
          ? 'worked'
          : today >= periodStart
            ? 'open'
            : 'future';
    const net = input.received ?? netPay(config, input.hours ?? minHours);
    const inWindow = (date: string) =>
      (i === 0 || date >= payday) && date < addDays(payday, 14);
    const extra = incomes
      .filter(income => inWindow(income.date))
      .reduce((sum, income) => sum + income.amount, 0);
    const own = charges.filter(
      c =>
        // Anything due before the very first payday is paid from cash on hand
        // and from that first pay.
        (i === 0 ? c.date < addDays(payday, 14) : c.date >= payday) &&
        c.date < addDays(payday, 14),
    );
    return {
      payday,
      nextPayday: addDays(payday, 14),
      periodStart,
      periodEnd,
      status,
      net,
      netIsEstimate: input.received === undefined,
      extra,
      own,
      totalCharges: own.reduce((sum, c) => sum - c.amount, 0),
    };
  });

  const result: Fortnight[] = [];
  let carried = available;
  drafts.forEach((draft, i) => {
    // Worst cumulative need from this pay to the horizon, later pays being
    // counted at the guaranteed hours only.
    let cumulativeCharges = 0;
    let cumulativeLaterNet = 0;
    let worstNeed = 0;
    for (let j = i; j < drafts.length; j++) {
      cumulativeCharges += drafts[j].totalCharges;
      if (j > i) {
        cumulativeLaterNet +=
          Math.min(drafts[j].net, netPay(config, minHours)) + drafts[j].extra;
      }
      worstNeed = Math.max(worstNeed, cumulativeCharges - cumulativeLaterNet);
    }
    const requiredNet = Math.max(0, worstNeed - carried - draft.extra);
    const fundsIn = carried + draft.net + draft.extra;

    // Each charge is paid from this pay first, then from what earlier pays
    // left over; a debt carried in is absorbed by this pay before anything.
    let own = draft.net + draft.extra + Math.min(0, carried);
    let prior = Math.max(0, carried);
    const planned: PlannedCharge[] = draft.own.map(charge => {
      const cost = -charge.amount;
      let coverage: Coverage;
      if (own >= cost) {
        own -= cost;
        coverage = { kind: 'covered', payday: draft.payday };
      } else if (Math.max(0, own) + prior >= cost) {
        prior -= cost - Math.max(0, own);
        own = Math.min(0, own);
        coverage =
          i > 0
            ? { kind: 'set-aside', payday: drafts[i - 1].payday }
            : { kind: 'covered', payday: draft.payday };
      } else {
        coverage = {
          kind: 'short',
          missing: cost - Math.max(0, own) - prior,
        };
        own = Math.min(0, own);
        prior = 0;
      }
      return { ...charge, coverage };
    });

    result.push({
      payday: draft.payday,
      nextPayday: draft.nextPayday,
      periodStart: draft.periodStart,
      periodEnd: draft.periodEnd,
      status: draft.status,
      net: draft.net,
      netIsEstimate: draft.netIsEstimate,
      charges: planned,
      totalCharges: draft.totalCharges,
      carriedIn: carried,
      requiredNet,
      thresholdHoursPerWeek: hoursForNet(config, requiredNet),
      margin: fundsIn - worstNeed,
      carriedOut: fundsIn - draft.totalCharges,
    });
    carried = fundsIn - draft.totalCharges;
  });
  return result;
}

/**
 * The balance the plan starts from. For an account linked to a bank that
 * sends none of its movements (a loan, a savings account), the bank's own:
 * the sum of its transactions misses them. Otherwise the book: a bank may give
 * the balance of an account in overdraft without its sign.
 */
export function planBalance(
  account:
    | {
        account_sync_source?: string | null;
        balance_current?: number | null;
      }
    | undefined,
  book: number | null,
  sendsMovements: boolean,
): number {
  return account?.account_sync_source &&
    account.balance_current != null &&
    !sendsMovements
    ? account.balance_current
    : (book ?? 0);
}

export type PlanWeek = {
  /** Sunday to Saturday. */
  start: string;
  end: string;
  /** Weeks from the week of today: -1 last week, 0 this week, 1 the next. */
  offset: number;
  payday: string;
  status: Fortnight['status'];
  /** Weekly hours this pay needs for every charge. */
  threshold: number;
  /** The hours to work: the threshold, never under the base. */
  hours: number;
  /** How far the threshold goes above the base. */
  aboveBase: number;
};

/** Each week of the plan with its hours, told from the week of `today`. */
export function planWeeks(
  plan: Fortnight[],
  today: string,
  base: number,
): PlanWeek[] {
  const thisWeek = weekStart(today);
  return plan.flatMap(fortnight =>
    [0, 7].map(shift => {
      const start = addDays(fortnight.periodStart, shift);
      const threshold = fortnight.thresholdHoursPerWeek;
      return {
        start,
        end: addDays(start, 6),
        offset: Math.round(daysBetween(thisWeek, start) / 7),
        payday: fortnight.payday,
        status: fortnight.status,
        threshold,
        hours: Math.max(threshold, base),
        aboveBase: Math.max(0, threshold - base),
      };
    }),
  );
}

/** Net gained by one more weekly hour above `hoursPerWeek`. */
export function netPerExtraHour(
  config: PayConfig,
  hoursPerWeek: number,
): number {
  return Math.round(
    (netPay(config, [hoursPerWeek + 1, hoursPerWeek + 1]) -
      netPay(config, [hoursPerWeek, hoursPerWeek])) /
      2,
  );
}

// ── Optimise ───────────────────────────────────────────────────────────────

export type Debt = {
  id: string;
  name: string;
  /** Cents owed. */
  balance: number;
  /** Annual rate, in percent. */
  rate: number;
  /** Required payment per month, in cents. */
  monthlyPayment: number;
  /** What is actually paid each month when it exceeds the minimum, in cents. */
  plannedPayment?: number;
  /**
   * One-off repayments already scheduled, in cents, by month ('YYYY-MM').
   * Their money is set aside by the schedule itself, not taken from the margin.
   */
  scheduledRepayments?: { month: string; amount: number }[];
  /** A payment deferral the creditor accepts, when there is one. */
  deferral?: { days: number; fee: number };
  /** True when the required payment is a floor that can be exceeded. */
  canPayMore?: boolean;
};

export type Reserve = {
  id: string;
  name: string;
  /** Cents it allows: an overdraft's limit, 0 for a savings account. */
  available: number;
  /** The account it is drawn on, whose balance adds to it or uses it. */
  accountId?: string;
  /** Flat fee for using it, in cents. */
  fee: number;
  /** Annual rate, in percent. */
  rate: number;
};

export type OptimiseOption = {
  id: string;
  kind: 'extra-hours' | 'minimum' | 'defer' | 'reserve' | 'partial';
  /** What it applies to; never sent to the AI engine. */
  subject: string;
  /** Cents it frees up. */
  frees: number;
  /** Cents it costs. */
  cost: number;
  hoursPerWeek?: number;
  days?: number;
  /** The debt or the reserve it applies to. */
  ref?: string;
  /** The pay the extra hours go to; the day the rest of a bill is paid. */
  payday?: string;
  /** Cents of a bill paid on its day, the rest later. */
  now?: number;
};

/**
 * What a reserve still offers. On an account the plan counts in the cash
 * (an overdraft), only what that account does not already use; on one it
 * does not count (a savings account), its balance as well.
 */
export function reserveRoom(
  reserve: Reserve,
  balance: number | undefined,
  countedInCash: boolean,
) {
  const own = balance ?? 0;
  return Math.max(
    0,
    reserve.available + (countedInCash ? Math.min(0, own) : own),
  );
}

export function listOptions({
  config,
  shortfall,
  debts,
  reserves,
  currentHoursPerWeek,
  maxExtraHoursPerWeek = 20,
  horizonDays = 30,
}: {
  config: PayConfig;
  shortfall: number;
  debts: Debt[];
  reserves: Reserve[];
  currentHoursPerWeek: number;
  maxExtraHoursPerWeek?: number;
  horizonDays?: number;
}): OptimiseOption[] {
  const options: OptimiseOption[] = [];
  const base = netPay(config, [currentHoursPerWeek, currentHoursPerWeek]);
  const needed = hoursForNet(config, base + shortfall);
  const extra = Math.min(
    maxExtraHoursPerWeek,
    Math.max(0, Math.ceil((needed - currentHoursPerWeek) * 2) / 2),
  );
  if (extra > 0) {
    const hours = currentHoursPerWeek + extra;
    options.push({
      id: 'extra-hours',
      kind: 'extra-hours',
      subject: '',
      frees: netPay(config, [hours, hours]) - base,
      cost: 0,
      hoursPerWeek: extra,
    });
  }
  for (const debt of debts) {
    // Falling back to the minimum frees the extra that was planned, at the
    // price of one more month of interest on it.
    const extraPlanned = (debt.plannedPayment ?? 0) - debt.monthlyPayment;
    if (extraPlanned > 0) {
      options.push({
        id: `minimum:${debt.id}`,
        kind: 'minimum',
        ref: debt.id,
        subject: debt.name,
        frees: Math.round((extraPlanned * horizonDays) / 30.4375),
        cost: Math.round((extraPlanned * debt.rate * horizonDays) / 100 / 365),
      });
    }
  }
  for (const debt of debts) {
    if (!debt.deferral) {
      continue;
    }
    const payments = Math.round(
      (debt.monthlyPayment * debt.deferral.days) / 30.4375,
    );
    options.push({
      id: `defer:${debt.id}`,
      kind: 'defer',
      ref: debt.id,
      subject: debt.name,
      frees: payments,
      cost:
        debt.deferral.fee +
        Math.round((debt.balance * debt.rate * debt.deferral.days) / 100 / 365),
      days: debt.deferral.days,
    });
  }
  for (const reserve of reserves) {
    if (reserve.available <= 0) {
      continue;
    }
    const used = Math.min(reserve.available, shortfall);
    options.push({
      id: `reserve:${reserve.id}`,
      kind: 'reserve',
      ref: reserve.id,
      subject: reserve.name,
      frees: used,
      cost:
        reserve.fee +
        Math.round((used * reserve.rate * horizonDays) / 100 / 365),
    });
  }
  return options;
}

// ── Debts and projects ─────────────────────────────────────────────────────

export type Project = {
  id: string;
  name: string;
  amount: number;
  /** Date the money is needed by, when there is one. */
  wished?: string;
  /** Start of the wished window, for a period or a given month. */
  wishedFrom?: string;
  /** How the user expressed the deadline. */
  deadline?: DeadlineKind;
  priority: number;
  /**
   * True when it is to be paid at its date before the debts: it then counts
   * as a charge of the pay before it, and weighs on the hours of that pay.
   */
  mandatory?: boolean;
};

/** A registered savings plan (TFSA, RRSP, FHSA, RESP) with room left. */
export type Saving = {
  id: string;
  name: string;
  /** Contribution room still available, in cents. */
  room: number;
};

export type DebtOutcome = {
  id: string;
  /** Month ('YYYY-MM') the debt is cleared, or null beyond the horizon. */
  paidOff: string | null;
  interest: number;
  /** Position in the accelerated repayment order, starting at 1. */
  order: number;
};

export type ProjectOutcome = {
  id: string;
  /** Month ('YYYY-MM') the project is fully funded, or null beyond horizon. */
  funded: string | null;
  /** Cents missing at the wished date; 0 when funded in time. */
  missingAtWish: number;
};

function addMonths(month: string, count: number): string {
  const [year, m] = month.split('-').map(Number);
  const total = year * 12 + (m - 1) + count;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/**
 * Month-by-month simulation: every debt gets its required payment; the
 * monthly margin and every freed payment go, in this order, to the emergency
 * fund, to the costliest debt that accepts extra payments, to registered
 * savings up to their contribution room, then to projects by priority.
 */
export function simulate({
  startMonth,
  debts,
  projects,
  monthlyMargin,
  emergencyFundTarget = 0,
  savings = [],
  horizonMonths = 240,
}: {
  startMonth: string;
  debts: Debt[];
  projects: Project[];
  monthlyMargin: number;
  /** Cents to set aside before any accelerated repayment; 0 for none. */
  emergencyFundTarget?: number;
  savings?: Saving[];
  horizonMonths?: number;
}): {
  debts: DebtOutcome[];
  projects: ProjectOutcome[];
  /** Month the emergency fund is complete; null when none or out of reach. */
  emergencyFund: string | null;
  /** Month each registered plan has used its room, or null beyond horizon. */
  savings: { id: string; filled: string | null }[];
} {
  const order = [...debts].sort((a, b) => b.rate - a.rate);
  const state = order.map(d => ({
    ...d,
    interest: 0,
    paidOff: null as string | null,
  }));
  const queue = [...projects]
    .sort((a, b) => a.priority - b.priority)
    .map(p => ({ ...p, saved: 0, funded: null as string | null, atWish: -1 }));
  const plans = savings.map(saving => ({
    ...saving,
    paid: 0,
    filled: null as string | null,
  }));
  let emergencySaved = 0;
  let emergencyFund: string | null = null;

  for (let i = 0; i < horizonMonths; i++) {
    const month = addMonths(startMonth, i);
    let extra = Math.max(0, monthlyMargin);

    for (const debt of state) {
      if (debt.balance <= 0) {
        // A cleared debt frees its payment for what comes next.
        extra += debt.monthlyPayment;
        continue;
      }
      const interest = Math.round((debt.balance * debt.rate) / 100 / 12);
      debt.interest += interest;
      debt.balance += interest;
      const paid = Math.min(debt.balance, debt.monthlyPayment);
      debt.balance -= paid;
      extra += debt.monthlyPayment - paid;
      for (const repayment of debt.scheduledRepayments ?? []) {
        if (repayment.month === month) {
          debt.balance -= Math.min(debt.balance, repayment.amount);
        }
      }
      if (debt.balance <= 0) {
        debt.paidOff = month;
      }
    }

    if (emergencySaved < emergencyFundTarget) {
      const saved = Math.min(emergencyFundTarget - emergencySaved, extra);
      emergencySaved += saved;
      extra -= saved;
      if (emergencySaved >= emergencyFundTarget) {
        emergencyFund = month;
      }
    }

    for (const debt of state) {
      if (extra <= 0) {
        break;
      }
      if (debt.balance <= 0 || debt.canPayMore === false) {
        continue;
      }
      const paid = Math.min(debt.balance, extra);
      debt.balance -= paid;
      extra -= paid;
      if (debt.balance <= 0) {
        debt.paidOff = month;
      }
    }

    // Registered savings only start once no debt can absorb the margin.
    const debtsWaiting = state.some(
      d => d.balance > 0 && d.canPayMore !== false,
    );
    for (const plan of plans) {
      if (debtsWaiting || extra <= 0 || plan.filled !== null) {
        continue;
      }
      const paid = Math.min(plan.room - plan.paid, extra);
      plan.paid += paid;
      extra -= paid;
      if (plan.paid >= plan.room) {
        plan.filled = month;
      }
    }

    for (const project of queue) {
      if (extra > 0 && project.funded === null) {
        const saved = Math.min(project.amount - project.saved, extra);
        project.saved += saved;
        extra -= saved;
        if (project.saved >= project.amount) {
          project.funded = month;
        }
      }
      if (project.wished && project.wished.slice(0, 7) === month) {
        project.atWish = project.saved;
      }
    }

    if (
      state.every(d => d.balance <= 0) &&
      queue.every(p => p.funded !== null) &&
      plans.every(p => p.filled !== null) &&
      emergencySaved >= emergencyFundTarget
    ) {
      break;
    }
  }

  return {
    debts: state.map((d, index) => ({
      id: d.id,
      paidOff: d.paidOff,
      interest: d.interest,
      order: index + 1,
    })),
    emergencyFund,
    savings: plans.map(p => ({ id: p.id, filled: p.filled })),
    projects: queue.map(p => ({
      id: p.id,
      funded: p.funded,
      missingAtWish:
        !p.wished || (p.funded !== null && p.funded <= p.wished.slice(0, 7))
          ? 0
          : Math.max(0, p.amount - Math.max(0, p.atWish)),
    })),
  };
}

// ── Snapshot for the server-side alerts ────────────────────────────────────

export type Snapshot = {
  generatedAt: string;
  today: string;
  /** Charges over the coming weeks, for the calendar feed. */
  events: { date: string; name: string; amount: number }[];
  /** One entry per coming Monday: the hours that week must reach. */
  mondays: {
    date: string;
    hours: number;
    periodStart: string;
    periodEnd: string;
    payday: string;
    margin: number;
  }[];
  shortfalls: { date: string; name: string; missing: number }[];
  /** The amounts the banks do not send, to ask of the user; a balance has no date. */
  toComplete: { id: string; name: string; date?: string }[];
};

/** What the server needs to send alerts without ever opening the budget. */
export function buildSnapshot({
  plan,
  occurrences,
  today,
  generatedAt,
  weeks = 6,
  toComplete = [],
}: {
  plan: Fortnight[];
  occurrences: Occurrence[];
  today: string;
  generatedAt: string;
  weeks?: number;
  toComplete?: { id: string; name: string; date?: string }[];
}): Snapshot {
  const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
  const firstMonday = addDays(today, (8 - weekday) % 7);
  const mondays: Snapshot['mondays'] = [];
  for (let i = 0; i < weeks; i++) {
    const date = addDays(firstMonday, 7 * i);
    const fortnight = plan.find(
      f => date >= f.periodStart && date <= f.periodEnd,
    );
    if (fortnight) {
      mondays.push({
        date,
        hours: fortnight.thresholdHoursPerWeek,
        periodStart: fortnight.periodStart,
        periodEnd: fortnight.periodEnd,
        payday: fortnight.payday,
        margin: fortnight.margin,
      });
    }
  }
  return {
    generatedAt,
    today,
    events: occurrences
      .filter(o => o.amount < 0)
      .map(o => ({ date: o.date, name: o.name, amount: -o.amount })),
    mondays,
    shortfalls: plan.flatMap(f =>
      f.charges.flatMap(c =>
        c.coverage.kind === 'short'
          ? [{ date: c.date, name: c.name, missing: c.coverage.missing }]
          : [],
      ),
    ),
    toComplete,
  };
}

// ── Deadlines, payments to make, verification against the bank ─────────────

export type DeadlineKind =
  | 'none'
  | 'date'
  | 'period'
  | 'month'
  | 'end-of-week'
  | 'end-of-month'
  | 'end-of-year';

/** Turns the way a deadline was expressed into a window of dates. */
export function resolveDeadline(
  kind: DeadlineKind,
  today: string,
  value: { date?: string; from?: string; month?: string } = {},
): { from?: string; by?: string } {
  const [year, month] = today.split('-').map(Number);
  const lastDay = (y: number, m: number) =>
    new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  switch (kind) {
    case 'date':
      return value.date ? { by: value.date } : {};
    case 'period':
      return value.from && value.date && value.from <= value.date
        ? { from: value.from, by: value.date }
        : {};
    case 'month': {
      if (!value.month || !/^\d{4}-\d{2}$/.test(value.month)) {
        return {};
      }
      const [y, m] = value.month.split('-').map(Number);
      return { from: `${value.month}-01`, by: lastDay(y, m) };
    }
    case 'end-of-week': {
      // Weeks run Monday to Sunday.
      const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
      return { by: addDays(today, (7 - weekday) % 7) };
    }
    case 'end-of-month':
      return { by: lastDay(year, month) };
    case 'end-of-year':
      return { by: `${year}-12-31` };
    default:
      return {};
  }
}

/**
 * Cents free for something else by `date`: the margin of the last pay
 * received on or before that date, every charge up to the horizon being paid.
 */
export function availableBy(plan: Fortnight[], date: string): number {
  const reached = plan.filter(f => f.payday <= date);
  return reached.length === 0 ? 0 : reached[reached.length - 1].margin;
}

/** Charges in the order to pay them: overdue first, then by due date. */
export function orderToPay<T extends Occurrence>(
  charges: T[],
  today: string,
): T[] {
  return [...charges].sort((a, b) => {
    const lateA = a.date < today ? 0 : 1;
    const lateB = b.date < today ? 0 : 1;
    return lateA - lateB || a.date.localeCompare(b.date) || a.amount - b.amount;
  });
}

export type BankMovement = {
  id: string;
  date: string;
  amount: number;
  account: string;
  /** The schedule Actual linked this transaction to, when it did. */
  scheduleId?: string | null;
};

const APPROX_TOLERANCE = 0.075;
const DAYS_BEFORE = 3;
const DAYS_AFTER = 5;

/**
 * For each expected payment, the bank transaction that proves it was made:
 * the one Actual linked to the schedule, else one of the same account and
 * amount within a few days. A transaction proves one payment only.
 */
export function verifyPayments(
  occurrences: Occurrence[],
  movements: BankMovement[],
): Record<string, BankMovement | null> {
  const used = new Set<string>();
  const result: Record<string, BankMovement | null> = {};
  const near = (occurrence: Occurrence, movement: BankMovement) => {
    const days = daysBetween(occurrence.date, movement.date);
    return days >= -DAYS_BEFORE && days <= DAYS_AFTER;
  };
  const closest = (occurrence: Occurrence, candidates: BankMovement[]) =>
    candidates
      .filter(m => !used.has(m.id) && near(occurrence, m))
      .sort(
        (a, b) =>
          Math.abs(daysBetween(occurrence.date, a.date)) -
          Math.abs(daysBetween(occurrence.date, b.date)),
      )[0] ?? null;

  const ordered = [...occurrences].sort((a, b) => a.date.localeCompare(b.date));
  // Links made by Actual are taken first: they are the strongest proof.
  for (const occurrence of ordered) {
    const linked = closest(
      occurrence,
      movements.filter(
        m => m.scheduleId != null && m.scheduleId === occurrence.scheduleId,
      ),
    );
    if (linked) {
      used.add(linked.id);
      result[occurrence.id] = linked;
    }
  }
  for (const occurrence of ordered) {
    if (result[occurrence.id]) {
      continue;
    }
    const tolerance = occurrence.approximate
      ? Math.abs(occurrence.amount) * APPROX_TOLERANCE
      : 0;
    const match = closest(
      occurrence,
      movements.filter(
        m =>
          occurrence.account !== undefined &&
          m.account === occurrence.account &&
          Math.abs(m.amount - occurrence.amount) <= tolerance,
      ),
    );
    if (match) {
      used.add(match.id);
    }
    result[occurrence.id] = match;
  }
  return result;
}

// ── Assistant: neutral labels in, real names out, every amount checked ─────

export type Entity = { label: string; name: string };

const WORD = /[\p{L}\p{N}]+/gu;
const MIN_WORD = 4;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The kind of thing a label names: « dette » for « dette 3 ». */
function kindOf(label: string): string {
  return label.replace(/\s*\d+$/, '');
}

/**
 * Words that point to one thing. A word shared by several names of the same
 * kind (« carte » in two debts) identifies none of them, and neither does a
 * word the labels themselves are made of. A word shared across kinds (a debt
 * and the payment that repays it) names one real thing: it goes to the first
 * entity listed.
 */
export function identifyingWords(entities: Entity[]): Map<string, string> {
  const labelWords = new Set(
    entities.flatMap(e => e.label.toLowerCase().match(WORD) ?? []),
  );
  const owners = new Map<string, string[]>();
  for (const entity of entities) {
    for (const word of new Set(entity.name.toLowerCase().match(WORD) ?? [])) {
      if (word.length >= MIN_WORD && !labelWords.has(word)) {
        owners.set(word, [...(owners.get(word) ?? []), entity.label]);
      }
    }
  }
  const result = new Map<string, string>();
  for (const [word, labels] of owners) {
    const kinds = labels.map(kindOf);
    if (new Set(kinds).size === kinds.length) {
      result.set(word, labels[0]);
    }
  }
  return result;
}

/** Replaces names, then identifying words, by their neutral label. */
export function anonymise(text: string, entities: Entity[]): string {
  // Names are swapped for markers first, so that a label already written is
  // never read again as ordinary words.
  const marker = (index: number) => `\u0000${index}\u0000`;
  const ordered = [...entities].sort((a, b) => b.name.length - a.name.length);
  let result = text;
  ordered.forEach((entity, index) => {
    if (entity.name.trim() !== '') {
      result = result.replace(
        new RegExp(escapeRegExp(entity.name), 'giu'),
        marker(index),
      );
    }
  });
  const words = identifyingWords(entities);
  result = result.replace(WORD, word => words.get(word.toLowerCase()) ?? word);
  ordered.forEach((entity, index) => {
    result = result.split(marker(index)).join(entity.label);
  });
  return result;
}

/** Puts the real names back where the engine wrote neutral labels. */
export function restoreNames(text: string, entities: Entity[]): string {
  let result = text;
  for (const entity of [...entities].sort(
    (a, b) => b.label.length - a.label.length,
  )) {
    result = result.replace(
      new RegExp(`(?:« ?)?${escapeRegExp(entity.label)}(?!\\d)(?: ?»)?`, 'giu'),
      `« ${entity.name} »`,
    );
  }
  return result;
}

/** Every number found anywhere in a facts sheet. */
export function numbersIn(value: unknown): number[] {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? [value] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap(numbersIn);
  }
  if (value && typeof value === 'object') {
    return Object.values(value).flatMap(numbersIn);
  }
  return [];
}

/**
 * Dollar amounts an answer states that the calculation cannot vouch for:
 * neither a known figure, nor the sum or difference of two known figures.
 */
export function unverifiedAmounts(answer: string, known: number[]): number[] {
  const cents = (dollars: number) => Math.round(dollars * 100);
  const allowed = new Set<number>([0]);
  const values = [...new Set(known.map(cents))];
  for (const a of values) {
    allowed.add(Math.abs(a));
    for (const b of values) {
      allowed.add(Math.abs(a + b));
      allowed.add(Math.abs(a - b));
    }
  }
  const stated = answer.match(/\d[\d\s  ]*(?:[.,]\d{1,2})?(?=\s*\$)/gu) ?? [];
  const unknown = stated
    .map(text => Number(text.replace(/[\s  ]/gu, '').replace(',', '.')))
    .filter(amount => Number.isFinite(amount) && !allowed.has(cents(amount)));
  return [...new Set(unknown)];
}

/**
 * The earliest moment an amount can be paid without touching any charge: the
 * first payday whose free margin covers it ('YYYY-MM-DD'), else the month
 * ('YYYY-MM') the average monthly margin gets there, else null.
 */
export function earliestFunding(
  plan: Fortnight[],
  amount: number,
  monthlyMargin: number,
): string | null {
  const reached = plan.find(fortnight => fortnight.margin >= amount);
  if (reached) {
    return reached.payday;
  }
  const last = plan[plan.length - 1];
  if (!last || monthlyMargin <= 0) {
    return null;
  }
  const months = Math.ceil((amount - Math.max(0, last.margin)) / monthlyMargin);
  return addMonths(last.payday.slice(0, 7), months);
}
